import { CSAT_SCORES, type CsatScore, type ThreadCsat } from '@zuruflex/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';
import { Textarea } from '../ui/textarea.js';

/** What a rated thread is allowed to carry in its comment. */
const MAX_COMMENT_CHARS = 500;

/**
 * Figures, not stars.
 *
 * Five stars is the mark this product already uses for rating a *car* —
 * `RatingStars` on a listing, a review card, a guest review, the owner's
 * reviews page — and "how did the help go?" is a different question that
 * must not wear the same badge. The phones settled that first; this is the
 * same decision on the web, so somebody who rates a thread on their laptop
 * and then opens the app recognises what they already answered.
 */
export interface CsatScoreMarkProps {
  score: number;
  /** Screen-reader text — the word for the score, which only the caller can translate. */
  label?: string;
  className?: string;
}

/** The answer, read-only: the numeral in ink, and the scale it was out of. */
export function CsatScoreMark({ score, label, className }: CsatScoreMarkProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 align-middle font-display text-sm font-bold tabular-nums text-text',
        className,
      )}
    >
      <span aria-hidden>{score}</span>
      <span className="text-xs font-medium text-text-muted" aria-hidden>
        /{CSAT_SCORES.length}
      </span>
      <span className="sr-only">{label ?? String(score)}</span>
    </span>
  );
}

/**
 * Picking a score, as a real radio group.
 *
 * Arrow keys move, Home and End jump to the ends, and the digits 1–5 set
 * directly — the same rating can be given by replying "4" in WhatsApp, and
 * the two paths should not feel like different products.
 *
 * **The chosen capsule is ink, not the accent**, exactly as it is on the
 * phones: the accent belongs to the one primary action on the panel, which
 * is the button that sends the answer.
 */
function ScoreScale({
  value,
  onChange,
  labels,
  disabled,
  className,
}: {
  value: CsatScore | null;
  onChange: (value: CsatScore) => void;
  labels: readonly [string, string, string, string, string];
  disabled?: boolean;
  className?: string;
}) {
  const move = (to: number) => {
    if (disabled) return;
    const clamped = Math.max(1, Math.min(CSAT_SCORES.length, to));
    onChange(clamped as CsatScore);
  };

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const current = value ?? 0;
    if (event.key >= '1' && event.key <= '5') {
      event.preventDefault();
      move(Number(event.key));
      return;
    }
    const step: Record<string, number> = {
      ArrowRight: current + 1,
      ArrowUp: current + 1,
      ArrowLeft: current - 1,
      ArrowDown: current - 1,
      Home: 1,
      End: CSAT_SCORES.length,
    };
    const next = step[event.key];
    if (next === undefined) return;
    event.preventDefault();
    move(next || 1);
  }

  return (
    <div
      role="radiogroup"
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
      className={cn('flex items-center gap-1.5', className)}
    >
      {CSAT_SCORES.map((score) => {
        const chosen = value === score;
        return (
          <button
            key={score}
            type="button"
            role="radio"
            aria-checked={chosen}
            aria-label={labels[score - 1]}
            disabled={disabled}
            // Roving tabindex: one stop for the whole group.
            tabIndex={chosen || (!value && score === 1) ? 0 : -1}
            onClick={() => move(score)}
            className={cn(
              'h-11 flex-1 rounded-full border font-display text-sm font-bold tabular-nums transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
              chosen
                ? 'border-text bg-text text-surface'
                : 'border-border-strong bg-surface text-text',
              disabled ? 'cursor-not-allowed opacity-50' : chosen ? '' : 'hover:bg-surface-hover',
            )}
          >
            {score}
          </button>
        );
      })}
    </div>
  );
}

export interface CsatPromptProps {
  /**
   * The thread's current score, or null on one that has never been asked.
   * An answered one draws the thank-you instead of the question — the
   * prompt has to be able to say "you already told us", because the panel
   * it sits in is still on screen after the answer goes.
   */
  csat?: ThreadCsat | null;
  onSubmit: (value: { score: CsatScore; comment?: string }) => void | Promise<void>;
  /** Absent means the prompt cannot be waved away. */
  onDismiss?: () => void;
  pending?: boolean;
  className?: string;
}

/**
 * "How did we do?", on the web.
 *
 * The counterpart of the phone apps' prompt, and deliberately the same
 * shape: five numbered capsules, an optional sentence, and a way out.
 * Somebody who rates a thread on their laptop and then opens the app should
 * recognise what they already answered — which is why the mark is the same
 * mark and not the stars a car is rated with.
 *
 * Where it legitimately differs is the submit: a browser has room for a
 * comment box and a Send, and the contract allows that difference because a
 * phone's conversation screen already spends its one primary action on the
 * composer's send key.
 *
 * The comment box only appears once a score is picked. The score is one
 * tap and arrives from nearly everyone; a textarea sitting open above the
 * scale turns a one-tap question into a form, and most of the sample is
 * lost to it.
 */
export function CsatPrompt({ csat, onSubmit, onDismiss, pending, className }: CsatPromptProps) {
  const { t } = useTranslation('common');
  const [score, setScore] = useState<CsatScore | null>(csat?.score ?? null);
  const [comment, setComment] = useState('');

  const answered = Boolean(csat?.answeredAt);

  const shell = cn('rounded-card border border-border-default bg-surface-raised p-4', className);

  if (answered) {
    return (
      <div className={shell}>
        <p className="flex items-center gap-2 text-sm font-semibold text-text">
          {t('csat.thanks')}
          {csat?.score ? (
            <CsatScoreMark score={csat.score} label={t(`csat.score.${csat.score}`)} />
          ) : null}
        </p>
      </div>
    );
  }

  return (
    <div className={shell}>
      <p className="font-display text-sm font-bold text-text">{t('csat.title')}</p>
      <p className="mt-0.5 text-xs text-text-muted">{t('csat.body')}</p>

      <ScoreScale
        value={score}
        onChange={setScore}
        // One label per point, 1 to 5, so a screen reader hears "Good"
        // rather than "4" — and hears it translated.
        labels={[
          t('csat.score.1'),
          t('csat.score.2'),
          t('csat.score.3'),
          t('csat.score.4'),
          t('csat.score.5'),
        ]}
        disabled={pending}
        className="mt-3"
      />

      {/*
        The two ends named, which is what makes a bare row of numerals a
        scale — the same two anchors the phones draw, in the same places.
      */}
      <div className="mt-1 flex items-center justify-between text-[11px] uppercase tracking-wide text-text-muted">
        <span>{t('csat.score.1')}</span>
        <span>{t('csat.score.5')}</span>
      </div>

      {score ? (
        <label className="mt-3 block">
          <span className="text-xs font-medium text-text-muted">{t('csat.comment')}</span>
          <Textarea
            value={comment}
            onChange={(event) => setComment(event.target.value.slice(0, MAX_COMMENT_CHARS))}
            placeholder={t('csat.commentPlaceholder')}
            rows={2}
            disabled={pending}
            className="mt-1 min-h-16 resize-none"
          />
        </label>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!score || pending}
          isLoading={pending}
          onClick={() => {
            if (!score) return;
            // An empty box is no comment at all, not an empty one — the
            // column is nullable and a blank string would read as a
            // customer who typed nothing on purpose.
            void onSubmit({ score, comment: comment.trim() || undefined });
          }}
        >
          {t('csat.submit')}
        </Button>
        {onDismiss ? (
          <Button type="button" size="sm" variant="ghost" onClick={onDismiss} disabled={pending}>
            {t('csat.dismiss')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
